import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './Gallery-v2.module.css';
import Button from '../components/Button';
import { digitalOceanSpacesService } from '../../services/digitalOceanSpacesService';
import { Image } from '../components/ImagePreviewModal';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

const GalleryV2: React.FC = () => {
  const [titleVisible, setTitleVisible] = useState<boolean>(false);
  const [items, setItems] = useState<Image[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [fullscreenOpen, setFullscreenOpen] = useState<boolean>(false);
  const [fullscreenImage, setFullscreenImage] = useState<Image | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const [isDesktop, setIsDesktop] = useState<boolean>(window.innerWidth > 1024);

  const titleRef = useRef<HTMLHeadingElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const ITEMS_PER_PAGE = 10;
  const PRELOAD_THRESHOLD = 9; // Load more when user reaches the 9th item

  // Track screen size for responsive image loading
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch initial batch of images
  const fetchInitialMedia = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await digitalOceanSpacesService.listGalleryFiles(ITEMS_PER_PAGE);

      if (response.success && response.files) {
        setItems(response.files);
        setHasMore(response.hasMore);
        setNextToken(response.nextToken);
      } else {
        setError(response.error || 'Failed to load gallery images');
      }
    } catch (err) {
      console.error('Error fetching media:', err);
      setError('Error loading gallery');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch more images
  const fetchMoreMedia = useCallback(async () => {
    if (!hasMore || loadingMore || !nextToken) return;

    try {
      setLoadingMore(true);

      const response = await digitalOceanSpacesService.listGalleryFiles(
        ITEMS_PER_PAGE,
        nextToken
      );

      if (response.success && response.files) {
        setItems(prev => [...prev, ...response.files]);
        setHasMore(response.hasMore);
        setNextToken(response.nextToken);
      }
    } catch (err) {
      console.error('Error fetching more media:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, nextToken]);

  // Initial load
  useEffect(() => {
    fetchInitialMedia();
  }, [fetchInitialMedia]);

  // Check if we need to load more when approaching the end
  useEffect(() => {
    if (currentIndex >= PRELOAD_THRESHOLD && hasMore && !loadingMore) {
      const loadedItemsCount = items.length;
      const isNearEnd = currentIndex >= loadedItemsCount - (ITEMS_PER_PAGE - PRELOAD_THRESHOLD);

      if (isNearEnd) {
        fetchMoreMedia();
      }
    }
  }, [currentIndex, items.length, hasMore, loadingMore, fetchMoreMedia]);

  // Title visibility observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTitleVisible(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handlePrevious = useCallback((): void => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
  }, [items.length]);

  const handleNext = useCallback((): void => {
    setCurrentIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
  }, [items.length]);

  const handleImageClick = (media: Image, index: number): void => {
    setFullscreenImage(media);
    setCurrentIndex(index);
    setFullscreenOpen(true);
    // Save current scroll position
    scrollPositionRef.current = window.scrollY;
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPositionRef.current}px`;
    document.body.style.width = '100%';
  };

  const handleCloseFullscreen = (): void => {
    setFullscreenOpen(false);
    setFullscreenImage(null);
    // Restore body scroll
    const scrollY = scrollPositionRef.current;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    // Restore scroll position
    window.scrollTo(0, scrollY);
  };

  const handleFullscreenPrevious = useCallback((): void => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
    setCurrentIndex(newIndex);
    setFullscreenImage(items[newIndex]);
  }, [currentIndex, items]);

  const handleFullscreenNext = useCallback((): void => {
    const newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    setFullscreenImage(items[newIndex]);
  }, [currentIndex, items]);

  // Select optimal image URL for fullscreen based on screen size
  const getFullscreenImageUrl = (image: Image): string => {
    if (isDesktop) {
      return image.originalPath;
    } else {
      return image.mediumPath;
    }
  };

  const handleSeeEntireGallery = (): void => {
    window.location.href = '/gallery';
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (fullscreenOpen) {
        if (e.key === 'Escape') {
          handleCloseFullscreen();
        } else if (e.key === 'ArrowLeft') {
          handleFullscreenPrevious();
        } else if (e.key === 'ArrowRight') {
          handleFullscreenNext();
        }
      } else {
        if (e.key === 'ArrowLeft') {
          handlePrevious();
        } else if (e.key === 'ArrowRight') {
          handleNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handlePrevious, handleNext, handleFullscreenPrevious, handleFullscreenNext, fullscreenOpen]);

  // Touch/swipe support for main carousel
  useEffect(() => {
    if (fullscreenOpen || !carouselRef.current) return;

    let touchStartX = 0;
    let touchEndX = 0;
    let touchStartY = 0;
    let touchEndY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      touchEndX = e.touches[0].clientX;
      touchEndY = e.touches[0].clientY;
    };

    const handleTouchEnd = () => {
      const swipeThreshold = 50;
      const diffX = touchStartX - touchEndX;
      const diffY = Math.abs(touchStartY - touchEndY);

      // Only trigger swipe if horizontal movement is greater than vertical
      if (Math.abs(diffX) > swipeThreshold && Math.abs(diffX) > diffY) {
        if (diffX > 0) {
          // Swiped left - next image
          handleNext();
        } else {
          // Swiped right - previous image
          handlePrevious();
        }
      }
    };

    const carousel = carouselRef.current;
    carousel.addEventListener('touchstart', handleTouchStart, { passive: true });
    carousel.addEventListener('touchmove', handleTouchMove, { passive: true });
    carousel.addEventListener('touchend', handleTouchEnd);

    return () => {
      carousel.removeEventListener('touchstart', handleTouchStart);
      carousel.removeEventListener('touchmove', handleTouchMove);
      carousel.removeEventListener('touchend', handleTouchEnd);
    };
  }, [fullscreenOpen, handlePrevious, handleNext]);

  // Touch/swipe support for fullscreen modal
  useEffect(() => {
    if (!fullscreenOpen) return;

    let touchStartX = 0;
    let touchEndX = 0;
    let touchStartY = 0;
    let touchEndY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      touchEndX = e.touches[0].clientX;
      touchEndY = e.touches[0].clientY;

      // Prevent default scrolling behavior
      e.preventDefault();
    };

    const handleTouchEnd = () => {
      const swipeThreshold = 50;
      const diffX = touchStartX - touchEndX;
      const diffY = Math.abs(touchStartY - touchEndY);

      // Only trigger swipe if horizontal movement is greater than vertical
      if (Math.abs(diffX) > swipeThreshold && Math.abs(diffX) > diffY) {
        if (diffX > 0) {
          // Swiped left - next image
          handleFullscreenNext();
        } else {
          // Swiped right - previous image
          handleFullscreenPrevious();
        }
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [fullscreenOpen, handleFullscreenPrevious, handleFullscreenNext]);

  return (
    <div className={`${styles.main} trowel-background`}>
      <h1
        ref={titleRef}
        className={titleVisible ? styles.titleVisible : ''}
      >
        Our Gallery
      </h1>

      {loading ? (
        <div className={styles.loadingContainer}>
          <p>Loading gallery...</p>
        </div>
      ) : error ? (
        <div className={styles.errorContainer}>
          <p>Error: {error}</p>
        </div>
      ) : items.length === 0 ? (
        <div className={styles.emptyContainer}>
          <p>No gallery images found.</p>
        </div>
      ) : (
        <>
          <div className={styles.carouselContainer} ref={carouselRef}>
            {/* Previous Button */}
            <button
              className={`${styles.carouselButton} ${styles.prevButton}`}
              onClick={handlePrevious}
              aria-label="Previous image"
              type="button"
            >
              <ChevronLeft size={32} />
            </button>

            {/* Carousel Track */}
            <div className={styles.carouselTrack}>
              <div
                className={styles.carouselSlides}
                style={{
                  transform: `translateX(-${currentIndex * 100}%)`,
                }}
              >
                {items.map((media, index) => (
                  <div key={media.name} className={styles.carouselSlide}>
                    <div
                      className={styles.mediaContainer}
                      onClick={() => handleImageClick(media, index)}
                      style={{ cursor: 'pointer' }}
                    >
                      {media.mediaType === 'video' ? (
                        <div className={styles.videoPlaceholder}>
                          <span>Video</span>
                        </div>
                      ) : (
                        <img
                          src={isDesktop ? media.mediumPath : media.thumbPath}
                          alt={media.name}
                          className={styles.mediaImage}
                          loading={index <= currentIndex + 2 ? 'eager' : 'lazy'}
                          decoding="async"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Button */}
            <button
              className={`${styles.carouselButton} ${styles.nextButton}`}
              onClick={handleNext}
              aria-label="Next image"
              type="button"
            >
              <ChevronRight size={32} />
            </button>
          </div>

          {/* Carousel Indicators */}
          {/* <div className={styles.carouselIndicators}>
            {items.map((_, index) => (
              <button
                key={index}
                className={`${styles.indicator} ${index === currentIndex ? styles.indicatorActive : ''}`}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Go to image ${index + 1}`}
                type="button"
              />
            ))}
          </div> */}

          {/* Counter */}
          {/* <div className={styles.carouselCounter}>
            {currentIndex + 1} / {items.length}
            {loadingMore && ' (Loading more...)'}
          </div> */}

          {/* See Entire Gallery button */}
          <div className={styles.seeAllContainer}>
            <Button
              onClick={handleSeeEntireGallery}
              variant="primary"
            >
              See Entire Gallery
            </Button>
          </div>
        </>
      )}

      {/* Fullscreen Modal */}
      {fullscreenOpen && fullscreenImage && (
        <div className={styles.fullscreenModal} onClick={handleCloseFullscreen}>
          <button
            className={styles.closeButton}
            onClick={handleCloseFullscreen}
            aria-label="Close fullscreen"
            type="button"
          >
            <X size={32} />
          </button>

          {/* Previous Button */}
          <button
            className={`${styles.fullscreenCarouselButton} ${styles.fullscreenPrevButton}`}
            onClick={(e) => {
              e.stopPropagation();
              handleFullscreenPrevious();
            }}
            aria-label="Previous image"
            type="button"
          >
            <ChevronLeft size={40} />
          </button>

          {/* Image */}
          <img
            src={getFullscreenImageUrl(fullscreenImage)}
            alt={fullscreenImage.name}
            className={styles.fullscreenImage}
            onClick={(e) => e.stopPropagation()}
            loading="eager"
          />

          {/* Next Button */}
          <button
            className={`${styles.fullscreenCarouselButton} ${styles.fullscreenNextButton}`}
            onClick={(e) => {
              e.stopPropagation();
              handleFullscreenNext();
            }}
            aria-label="Next image"
            type="button"
          >
            <ChevronRight size={40} />
          </button>

          {/* Image Counter */}
          <div className={styles.fullscreenCounter}>
            {currentIndex + 1} / {items.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryV2;
